// PresentationFrame.tsx — THE standard presentation surface on a board.
//
// Erez-locked 2026-07-25: "it's always like a pitch deck, it's always two
// views." One toggle above the frame:
//
//   FILM   — the narrated Remotion video, reveals landing on the words.
//   SLIDES — click through one page at a time, exactly like the old deck.
//
// SLIDES shows the SAME frames the PDF is built from — real Remotion stills at
// 2x, not a PDF.js embed. Deliberate: an embedded PDF viewer inside a React
// Flow canvas fights the canvas for wheel and click events, ships a heavy
// worker, and renders its own chrome we cannot style. Images give identical
// pages, stay sharp, and cost nothing. The downloadable PDF is built from these
// very stills, so the two views can never disagree.
//
// CHROME: both views share ONE light bar that sits BELOW the content, never on
// top of it (Erez, 2026-07-25). The native <video controls> bar was rejected
// twice — it is black, it carries a gradient scrim, and it covers the bottom of
// the frame. So we drive the video ourselves and the two views look identical.
//
// Same React-Flow traps as its siblings (pointerEvents auto, nowheel/nodrag/
// nopan) so clicking through slides never pans the canvas.

import { useCallback, useEffect, useRef, useState } from "react";

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

type View = "film" | "slides";

export function PresentationFrame({
  videoSrc,
  poster,
  slidesBase,
  slideCount,
  pdf,
  label,
  caption,
  width = 1500,
  aspect = 16 / 9,
}: {
  /** Omit for a SLIDES-ONLY presentation (a client PDF with no film). The
   *  Film tab then disappears and the frame opens on the pages. */
  videoSrc?: string;
  poster?: string;
  /** R2 prefix holding slide-01.jpg … slide-NN.jpg */
  slidesBase: string;
  slideCount: number;
  pdf?: string;
  label?: string;
  caption?: string;
  width?: number;
  /** Content aspect (w/h). Defaults to 16:9 for decks and film. A PORTRAIT
   *  document (A4/Letter pages) must pass ~0.773 or `contain` fits by height
   *  and the page shrinks to a stamp between two grey margins. */
  aspect?: number;
}) {
  // The content area is EXACTLY 16:9 and the bar adds its height on top.
  // Getting this wrong letterboxes: a content box of 1500x790 is ratio 1.90
  // against 16:9 content at 1.78, so `contain` fits by height and leaves ~48px
  // of background down each side — invisible on the light slides, an obvious
  // black strip on the film. Derive, never hardcode a frame height.
  const BAR = 54;
  const hasFilm = Boolean(videoSrc);
  const contentH = Math.round(width / aspect);
  const frameH = contentH + BAR;
  const [view, setView] = useState<View>(hasFilm ? "film" : "slides");
  const [i, setI] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);

  const go = useCallback(
    (d: number) => setI((n) => Math.min(slideCount - 1, Math.max(0, n + d))),
    [slideCount],
  );

  // Arrow keys work while the slides view is open — same as the deck.
  useEffect(() => {
    if (view !== "slides") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, go]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) void v.play(); else v.pause();
  }, []);

  const seek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    const r = e.currentTarget.getBoundingClientRect();
    v.currentTime = ((e.clientX - r.left) / r.width) * v.duration;
  }, []);

  const src = `${slidesBase}/slide-${String(i + 1).padStart(2, "0")}.jpg`;
  const tabStyle = (active: boolean): React.CSSProperties => ({
    pointerEvents: "auto",
    fontFamily: MONO,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    padding: "7px 18px",
    borderRadius: 999,
    border: "none",
    cursor: "pointer",
    background: active ? "#1B211E" : "transparent",
    color: active ? "#FFFFFF" : "#8C948E",
  });

  return (
    <div style={{ width, fontFamily: FONT, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, paddingInline: 4 }}>
        <div
          className="nodrag nopan"
          onPointerDown={(e) => e.stopPropagation()}
          style={{ pointerEvents: "auto", display: "flex", gap: 4, background: "#EFEDE8", padding: 4, borderRadius: 999 }}
        >
          {hasFilm && (
            <button style={tabStyle(view === "film")} onClick={() => setView("film")}>Film</button>
          )}
          <button style={tabStyle(view === "slides")} onClick={() => setView("slides")}>Slides</button>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "baseline" }}>
          <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "#8C948E" }}>
            {label}
          </span>
          {pdf && (
            <a href={pdf} target="_blank" rel="noreferrer" className="nodrag nopan"
              style={{ pointerEvents: "auto", fontFamily: MONO, fontSize: 11, color: "#8C948E", textDecoration: "none" }}
              onPointerDown={(e) => e.stopPropagation()}>PDF ↓</a>
          )}
        </div>
      </div>

      <div
        className="nowheel nodrag nopan"
        data-zoom
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          pointerEvents: "auto", width, height: frameH, overflow: "hidden",
          background: view === "film" ? "#0E1512" : "#F2F7FD",
          border: "1px solid #E3DED4", borderRadius: 18,
          boxShadow: "0 10px 30px rgba(27,33,30,0.05)", boxSizing: "border-box",
          display: "flex", flexDirection: "column",
        }}
      >
      <div style={{ height: contentH, position: "relative" }}>
        {view === "film" ? (
          <video
            ref={videoRef}
            src={videoSrc}
            poster={poster}
            preload="none"
            onClick={togglePlay}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
            onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
            style={{ width: "100%", height: "100%", display: "block", objectFit: "contain", background: "#0E1512", cursor: "pointer" }}
          />
        ) : (
          /* click the page to advance, exactly like the deck */
          <img
            src={src}
            alt={`Slide ${i + 1}`}
            onClick={() => go(1)}
            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", cursor: "pointer" }}
          />
        )}
      </div>

      {/* ONE bar, both views, BELOW the content — never over it */}
      <div style={{ height: BAR, flexShrink: 0, display: "flex", alignItems: "center", gap: 16,
        padding: "0 20px", background: "#FFFFFF", borderTop: "1px solid #ECE7DF" }}>
        {view === "film" ? (
          <>
            <button onClick={togglePlay} style={navBtn(false)}>{playing ? "❚❚" : "▶"}</button>
            <div
              onClick={seek}
              style={{ flex: 1, height: 6, borderRadius: 999, background: "#ECE7DF", cursor: "pointer", position: "relative" }}
            >
              <div style={{ position: "absolute", inset: 0, width: `${dur ? (t / dur) * 100 : 0}%`,
                background: "#3B82C8", borderRadius: 999 }} />
            </div>
            <span style={{ fontFamily: MONO, fontSize: 12, color: "#4A534D", whiteSpace: "nowrap" }}>
              {clock(t)} / {clock(dur)}
            </span>
          </>
        ) : (
          <>
            <span style={{ fontFamily: MONO, fontSize: 13, color: "#4A534D" }}>{i + 1} / {slideCount}</span>
            <span style={{ flex: 1 }} />
            <button onClick={() => go(-1)} disabled={i === 0} style={navBtn(i === 0)}>←</button>
            <button onClick={() => go(1)} disabled={i === slideCount - 1} style={navBtn(i === slideCount - 1)}>→</button>
          </>
        )}
      </div>
      </div>

      {caption && (
        <div style={{ fontFamily: FONT, fontSize: 15, color: "#4A534D", lineHeight: 1.5, paddingInline: 4, maxWidth: width }}>
          {caption}
        </div>
      )}
    </div>
  );
}

const clock = (s: number) => {
  if (!Number.isFinite(s) || s <= 0) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const navBtn = (disabled: boolean): React.CSSProperties => ({
  pointerEvents: "auto", fontFamily: MONO, fontSize: 15, lineHeight: 1,
  padding: "8px 16px", borderRadius: 10, cursor: disabled ? "default" : "pointer",
  border: "1px solid #E3DED4", background: "#FFFFFF", color: disabled ? "#C9CFCB" : "#1B211E",
});
